/* EarthBased add-to-cart confirmation + cart drawer + safety net
   23 Aug 2026 (v17). Replaces v9 IN PLACE, keeping the filename so existing
   references keep working. Never load two copies: each would decide nothing
   was added and each would POST /cart/add.js, adding the item twice.

   BEHAVIOUR
   - Add To Cart  -> the button briefly reads '✓ Added' and a small pill shows
                     'Item added to cart · View cart' near the top. Nothing
                     opens, nothing blocks the page.
   - Cart icon    -> opens the slide-in drawer, with quantity +/- and Remove.

   v17 - THE HEADER BADGE SELECTOR WAS WRONG (see refreshCartCount below).
   Removing an item left the header count showing the old number, so the store
   read as if the products were still in the cart.

   v16 - PILL POSITION IS MEASURED, NOT GUESSED
   The pill used a hard-coded top offset, which risked overlapping a sticky
   header on viewports that were never tested. It now measures any fixed or
   sticky header at paint time and sits 8px below it, falling back to 16px from
   the top when nothing is pinned (this theme's header is position:relative, so
   that is the normal case). Works on any viewport without magic numbers.

   v15 - THE THEME'S OWN SIDE-CART IS HARD-SUPPRESSED
   Measured in-browser: after Add To Cart, html gained .side-cart-open and the
   theme's own .side-cart panel appeared. That was the drawer the owner kept
   seeing - not this file's. v10-v12 masked it because they hid it on the way
   to opening their own drawer; once v13 stopped opening a drawer on add,
   nothing suppressed it any more. Two belts, because a class alone was not
   enough:
     1. CSS '.side-cart{display:none!important}' injected at load.
     2. A MutationObserver stripping .side-cart-open off <html>/<body> whenever
        the theme re-adds it - that class also locks page scroll.
   The theme's side-cart is permanently inert; this drawer is the only cart UI.

   v14 - WHY CART_LINK IS NARROW
   v13's selector included [class*="cart-icon"] and .action-cart. The Add To
   Cart button has a cart icon inside it, so clicking it matched as a 'cart
   link' and opened the drawer, skipping the add. CART_LINK is now anchors to
   /cart only, and anything inside a /cart/add form is excluded first.

   HISTORY OF THE CONFIRMATION DESIGN - READ BEFORE 'IMPROVING' IT
   v10/v11 opened the whole drawer on every add; the owner found that
   interrupting. v12 showed a detail card because Baymard's 'Added to Cart
   Confirmation' research finds confirmations omitting item detail "risk users
   losing confidence". The owner reviewed it and still wanted something
   minimal, so the pill is deliberately terse. Considered trade-off: the
   shopper verifies via the cart icon. If wrong items start reaching checkout,
   restoring the v12 card is the first fix.

   WHY THE SAFETY NET IS STILL HERE
   The Interakt SDK is injected twice via two shop-level ScriptTags - you can
   see the second copy fail in the console with "Uncaught SyntaxError:
   Identifier 'showPoweredBy' has already been declared". When its copy wins
   the load race it binds a click listener on Add To Cart, calls
   preventDefault(), then dies - so the theme's own handler never runs and
   nothing is added. Intermittent. This file never calls preventDefault on the
   add: it lets the theme go first, polls the cart, and only adds the item
   itself if nothing landed within MAX_WAIT_MS. Polls are chained (each waits
   for the previous response, ~350ms on this store) so they cannot pile up.

   NO CSS TRANSITIONS - DELIBERATE, DO NOT ADD THEM BACK
   Measured 21 Aug 2026: CSS transitions do not progress on product pages here.
   The main thread stalls for seconds (same Interakt problem), so a transition
   sits frozen on its first frame forever - which left an animated panel parked
   off-screen even with the open class applied. Everything here appears
   instantly. Restore animation only once the main thread is healthy.

   INTERACTION WITH THE PINCODE GATE - IMPORTANT
   eb-pincode.js sets window.EB_PINCODE_LOCKED = true while a restricted-vendor
   product waits for a serviceable pincode. This script registers its listener
   FIRST, so it cannot see the gate's stopImmediatePropagation(). It therefore
   checks the shared flag before adding anything AND before confirming.

   SCOPE - READ BEFORE EDITING
   Only ever acts on the theme's Add To Cart button. The Shiprocket BUY NOW
   (fastrr) button lives inside the SAME form[action=/cart/add], so matching is
   an explicit allow-list plus a hard deny list. Do not widen the allow-list.

   OTHER GOTCHAS ON THIS STORE
   - window.fetch, XMLHttpRequest and HTMLFormElement.prototype.submit are ALL
     monkey-patched by installed apps. Do not rely on form.submit().
   - Do NOT put a /checkout link anywhere here - Shiprocket rewrites checkout
     links and injects its widget into them. Link to /cart only.
   - The theme ships a lot of !important CSS, so the drawer's open state is set
     as inline !important styles rather than through the cascade.
   - Line edits use /cart/change.js with a 1-based `line`, NOT variant id: the
     same variant can occupy two lines when line-item properties differ. */
(function () {
  'use strict';
  var POLL_EVERY_MS = 200, MAX_WAIT_MS = 2200, PILL_MS = 3500, BTN_MS = 2000;
  var ALLOW = '.add-to-cart-button, button[name="add"], input[name="add"]';
  var DENY  = '[class*="fastrr"], [id*="fastrr"], [class*="shiprocket"], [id*="shiprocket"], [class*="pickrr"], [id*="pickrr"], [class*="buy-now"], [id*="buy-now"], [class*="buynow"], [data-shopify="payment-button"], .shopify-payment-button';
  var CART_LINK = 'a[href$="/cart"], a[href*="/cart?"]';
  var busy=false, editing=false;
  var root=null,panel=null,backdrop=null,listEl=null,totalEl=null,titleEl=null;
  var pillEl=null, pillTimer=null, lastFocus=null, scrollY=0, cssDone=false;
  function gateLocked(){return window.EB_PINCODE_LOCKED===true;}
  function denied(el){if(!el)return false;if(el.closest&&el.closest(DENY))return true;return (el.textContent||'').trim().toLowerCase().indexOf('buy now')>-1;}
  function esc(s){return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];});}
  function money(c){var v=(c||0)/100;try{return new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(v);}catch(e){return '₹'+Math.round(v);}}
  function getCart(){return fetch('/cart.js',{credentials:'same-origin',headers:{'Accept':'application/json'}}).then(function(r){return r.json();}).catch(function(){return null;});}
  function changeLine(line,qty){
    return fetch('/cart/change.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({line:line,quantity:qty})})
      .then(function(r){ if(!r.ok)throw new Error('change failed'); return r.json(); })
      .catch(function(){ return null; });
  }
  function thumb(u){return u?u.replace(/(\.[a-z]+)(\?|$)/i,'_120x$1$2'):'';}
  function styles(){
    if(cssDone)return; cssDone=true;
    var css=document.createElement('style');
    css.textContent=[
      '.side-cart{display:none!important}',
      '.ebt{position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:2147483001;display:none;align-items:center;gap:8px;background:#253d4e;color:#fff;border-radius:999px;padding:9px 16px;font-size:13.5px;font-weight:500;font-family:inherit;box-shadow:0 6px 20px rgba(0,0,0,.22);white-space:nowrap;max-width:calc(100vw - 24px)}',
      '.ebt.on{display:flex}',
      '.ebt-tick{flex:0 0 16px;width:16px;height:16px;border-radius:50%;background:#6fbf7f;color:#123;font-size:10px;line-height:16px;text-align:center;font-weight:700}',
      '.ebt-cart{color:#bfe6c9;text-decoration:underline;cursor:pointer;background:none;border:0;font:inherit;padding:0}',
      '@media(max-width:749px){.ebt{font-size:13px;padding:8px 14px}}',
      '.ebd-root{position:fixed;inset:0;z-index:2147483000;pointer-events:none;font-family:inherit}',
      '.ebd-root.ebd-open{pointer-events:auto}',
      '.ebd-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.45);opacity:0}',
      '.ebd-panel{position:absolute;top:0;right:0;height:100%;width:min(390px,100%);background:#fff;color:#253d4e;display:flex;flex-direction:column;box-shadow:-8px 0 32px rgba(0,0,0,.18);transform:translateX(100%)}',
      '.ebd-head{display:flex;align-items:center;gap:12px;padding:16px 18px;border-bottom:1px solid #e7eae9;flex:0 0 auto}',
      '.ebd-title{font-size:15px;font-weight:600;margin:0;flex:1 1 auto}',
      '.ebd-x{border:0;background:transparent;font-size:24px;line-height:1;cursor:pointer;color:#5b6b73;padding:4px 6px;border-radius:6px}',
      '.ebd-body{flex:1 1 auto;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:8px 18px}',
      '.ebd-body.ebd-busy{opacity:.55;pointer-events:none}',
      '.ebd-item{display:flex;gap:12px;padding:14px 0;border-bottom:1px solid #f0f2f1}',
      '.ebd-item img{flex:0 0 62px;width:62px;height:62px;object-fit:contain;border-radius:8px;background:#f7f8f8}',
      '.ebd-meta{flex:1 1 auto;min-width:0}',
      '.ebd-name{font-size:13.5px;line-height:1.35;margin:0 0 4px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
      '.ebd-name a{color:inherit;text-decoration:none}',
      '.ebd-vari{font-size:12px;color:#7b8a91;margin:0 0 6px}',
      '.ebd-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}',
      '.ebd-stepper{display:inline-flex;align-items:center;border:1px solid #dde4e0;border-radius:8px;overflow:hidden}',
      '.ebd-step{width:30px;height:30px;border:0;background:#fff;color:#253d4e;font-size:16px;line-height:1;cursor:pointer}',
      '.ebd-step:hover{background:#f4f7f5}',
      '.ebd-q{min-width:26px;text-align:center;font-size:13px}',
      '.ebd-price{font-size:13px;font-weight:600;margin-left:auto}',
      '.ebd-rm{border:0;background:none;color:#a2635f;font-size:12px;text-decoration:underline;cursor:pointer;padding:0}',
      '.ebd-foot{flex:0 0 auto;border-top:1px solid #e7eae9;padding:16px 18px;display:grid;gap:10px}',
      '.ebd-sub{display:flex;justify-content:space-between;font-size:14px;font-weight:600}',
      '.ebd-note{font-size:11.5px;color:#7b8a91;margin:-4px 0 2px}',
      '.ebd-btn{display:block;text-align:center;padding:12px 14px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;cursor:pointer;border:1px solid transparent;width:100%}',
      '.ebd-primary{background:#4a8e5a;color:#fff}',
      '.ebd-secondary{background:#fff;color:#4a8e5a;border-color:#cfe4d6}',
      '.ebd-empty{padding:28px 4px;color:#7b8a91;font-size:14px;text-align:center}'
    ].join('');
    (document.head||document.documentElement).appendChild(css);
  }
  function stripThemeClass(){
    var d=document.documentElement;
    if(d&&d.classList.contains('side-cart-open'))d.classList.remove('side-cart-open');
    if(document.body&&document.body.classList.contains('side-cart-open'))document.body.classList.remove('side-cart-open');
  }
  function suppressThemeCart(){
    stripThemeClass();
    try{
      var mo=new MutationObserver(stripThemeClass);
      mo.observe(document.documentElement,{attributes:true,attributeFilter:['class']});
      if(document.body){ mo.observe(document.body,{attributes:true,attributeFilter:['class']}); }
      else { document.addEventListener('DOMContentLoaded',function(){ stripThemeClass(); mo.observe(document.body,{attributes:true,attributeFilter:['class']}); }); }
    }catch(e){}
  }
  /* Sit below whatever is actually pinned to the top, on any viewport. */
  function pillTopPx(){
    var top=16;
    try{
      var cands=document.querySelectorAll('header, .header, #shopify-section-header, .topbar, [class*="sticky"]');
      for(var i=0;i<cands.length;i++){
        var el=cands[i], s=getComputedStyle(el);
        if(s.position!=='fixed'&&s.position!=='sticky')continue;
        if(s.display==='none'||s.visibility==='hidden')continue;
        var r=el.getBoundingClientRect();
        if(r.height<8||r.top>80)continue;
        if(r.bottom+8>top)top=Math.round(r.bottom)+8;
      }
    }catch(e){}
    return Math.min(top, Math.round((window.innerHeight||600)*0.3));
  }
  function hidePill(){ if(pillEl)pillEl.classList.remove('on'); clearTimeout(pillTimer); }
  function showPill(){
    styles();
    if(!pillEl){
      pillEl=document.createElement('div');
      pillEl.className='ebt';
      pillEl.setAttribute('role','status');
      pillEl.setAttribute('aria-live','polite');
      pillEl.innerHTML='<span class="ebt-tick" aria-hidden="true">✓</span><span>Item added to cart</span>'+
        '<span aria-hidden="true">·</span><button type="button" class="ebt-cart" data-ebt-cart>View cart</button>';
      document.body.appendChild(pillEl);
      pillEl.addEventListener('click',function(e){ if(e.target.closest('[data-ebt-cart]')){ e.preventDefault(); hidePill(); openDrawer(); } });
    }
    pillEl.style.top=pillTopPx()+'px';
    pillEl.classList.add('on');
    clearTimeout(pillTimer); pillTimer=setTimeout(hidePill,PILL_MS);
  }
  function flashButton(btn){
    if(!btn||btn.getAttribute('data-ebt-busy')==='1')return;
    var label=btn.querySelector('span')||btn;
    var original=label.innerHTML;
    btn.setAttribute('data-ebt-busy','1');
    label.innerHTML='✓ Added';
    setTimeout(function(){ label.innerHTML=original; btn.setAttribute('data-ebt-busy',''); },BTN_MS);
  }
  function buildDrawer(){
    if(root)return root; styles();
    root=document.createElement('div'); root.className='ebd-root'; root.setAttribute('aria-hidden','true');
    root.innerHTML='<div class="ebd-backdrop" data-ebd-close></div>'+
      '<aside class="ebd-panel" role="dialog" aria-modal="true" aria-label="Shopping cart">'+
      '<div class="ebd-head"><h2 class="ebd-title">Your cart</h2>'+
      '<button type="button" class="ebd-x" data-ebd-close aria-label="Close cart">×</button></div>'+
      '<div class="ebd-body"><div class="ebd-empty">Loading…</div></div><div class="ebd-foot"></div></aside>';
    document.body.appendChild(root);
    panel=root.querySelector('.ebd-panel'); backdrop=root.querySelector('.ebd-backdrop');
    listEl=root.querySelector('.ebd-body'); totalEl=root.querySelector('.ebd-foot'); titleEl=root.querySelector('.ebd-title');
    root.addEventListener('click',function(e){
      if(e.target.closest('[data-ebd-close]')){ closeDrawer(); return; }
      var ctl=e.target.closest('[data-ebd-line]');
      if(!ctl||editing)return;
      e.preventDefault();
      var line=parseInt(ctl.getAttribute('data-ebd-line'),10);
      var qty=parseInt(ctl.getAttribute('data-ebd-qty'),10);
      if(!line||isNaN(qty))return;
      editing=true; listEl.classList.add('ebd-busy');
      changeLine(line,qty).then(function(cart){
        editing=false; listEl.classList.remove('ebd-busy');
        if(cart){ renderDrawer(cart); refreshCartCount(cart); }
      });
    });
    document.addEventListener('keydown',function(e){ if(e.key==='Escape'&&root.classList.contains('ebd-open'))closeDrawer(); });
    return root;
  }
  function lockScroll(){scrollY=window.pageYOffset||document.documentElement.scrollTop||0;document.body.style.position='fixed';document.body.style.top=(-scrollY)+'px';document.body.style.left='0';document.body.style.right='0';document.body.style.width='100%';}
  function unlockScroll(){document.body.style.position='';document.body.style.top='';document.body.style.left='';document.body.style.right='';document.body.style.width='';window.scrollTo(0,scrollY);}
  function openDrawer(){
    buildDrawer(); hidePill();
    if(!root.classList.contains('ebd-open')){
      lastFocus=document.activeElement; lockScroll();
      root.classList.add('ebd-open'); root.setAttribute('aria-hidden','false');
      panel.style.setProperty('transition','none','important');
      backdrop.style.setProperty('transition','none','important');
      panel.style.setProperty('transform','translateX(0)','important');
      backdrop.style.setProperty('opacity','1','important');
      var x=root.querySelector('.ebd-x'); if(x){try{x.focus();}catch(e){}}
    }
    getCart().then(function(c){renderDrawer(c);refreshCartCount(c);});
  }
  function closeDrawer(){
    if(!root)return;
    root.classList.remove('ebd-open'); root.setAttribute('aria-hidden','true');
    panel.style.setProperty('transform','translateX(100%)','important');
    backdrop.style.setProperty('opacity','0','important');
    unlockScroll();
    if(lastFocus&&lastFocus.focus){try{lastFocus.focus();}catch(e){}}
  }
  function renderDrawer(cart){
    buildDrawer();
    if(!cart||!cart.items||!cart.items.length){
      titleEl.textContent='Your cart';
      listEl.innerHTML='<div class="ebd-empty">Your cart is empty.</div>';
      totalEl.innerHTML='<a class="ebd-btn ebd-secondary" href="/collections/all">Start shopping</a>';
      return;
    }
    titleEl.textContent='Your cart ('+cart.item_count+')';
    var html='';
    for(var i=0;i<cart.items.length;i++){
      var it=cart.items[i];
      var line=i+1;
      var img=thumb(it.image);
      var variant=(it.variant_title&&it.variant_title!=='Default Title')?it.variant_title:'';
      html+='<div class="ebd-item">'+(img?'<img src="'+esc(img)+'" alt="" loading="eager" decoding="async" width="62" height="62">':'')+
        '<div class="ebd-meta">'+
        '<p class="ebd-name"><a href="'+esc(it.url||'#')+'">'+esc(it.product_title||it.title)+'</a></p>'+
        (variant?'<p class="ebd-vari">'+esc(variant)+'</p>':'')+
        '<div class="ebd-row">'+
          '<span class="ebd-stepper">'+
            '<button type="button" class="ebd-step" data-ebd-line="'+line+'" data-ebd-qty="'+(it.quantity-1)+'" aria-label="Decrease quantity">−</button>'+
            '<span class="ebd-q">'+esc(it.quantity)+'</span>'+
            '<button type="button" class="ebd-step" data-ebd-line="'+line+'" data-ebd-qty="'+(it.quantity+1)+'" aria-label="Increase quantity">+</button>'+
          '</span>'+
          '<button type="button" class="ebd-rm" data-ebd-line="'+line+'" data-ebd-qty="0">Remove</button>'+
          '<span class="ebd-price">'+money(it.final_line_price)+'</span>'+
        '</div></div></div>';
    }
    listEl.innerHTML=html;
    totalEl.innerHTML='<div class="ebd-sub"><span>Subtotal</span><span>'+money(cart.total_price)+'</span></div>'+
      '<p class="ebd-note">Shipping and taxes calculated at checkout.</p>'+
      '<a class="ebd-btn ebd-primary" href="/cart">View cart &amp; checkout</a>'+
      '<button type="button" class="ebd-btn ebd-secondary" data-ebd-close>Continue shopping</button>';
  }
  /* v17 - THE HEADER BADGE SELECTOR WAS WRONG, SO REMOVALS NEVER SHOWED
     This theme's badge is <span class="item-count cart-item-count"> (see
     sections/header.liquid) and the theme ships btyCartItemCount(n) in
     theme.js, which writes the number AND toggles .hidden at zero.
     v16 and earlier looked for '.cart-count, [data-cart-count], .cart-number,
     .header-icons .cart .count' - none of which exist in this theme - so this
     function silently matched nothing. Remove an item in the drawer and the
     header kept showing the old number, which reads to a shopper as "the
     products are still in my cart". Prefer the theme's own updater so the
     zero-state .hidden class is handled the same way the theme handles it;
     fall back to writing the nodes directly if theme.js has not loaded yet. */
  function refreshCartCount(cart){
    if(!cart)return;
    var n=Number(cart.item_count)||0;
    if(typeof window.btyCartItemCount==='function'){
      try{ window.btyCartItemCount(n); return; }catch(e){}
    }
    var nodes=document.querySelectorAll('.cart-item-count, .cart-count, [data-cart-count], .cart-number');
    Array.prototype.forEach.call(nodes,function(el){
      el.innerHTML=n;
      if(n<1)el.classList.add('hidden'); else el.classList.remove('hidden');
    });
  }
  /* Re-read the real cart and repaint the badge. Used on first paint and on
     bfcache restore: pressing Back after emptying the cart replays a cached
     page whose badge is baked into the HTML, which looks like the same bug. */
  function syncCount(){ getCart().then(refreshCartCount); }
  function payloadFrom(form){
    var id=form.querySelector('[name="id"]'); if(!id||!id.value)return null;
    var q=form.querySelector('[name="quantity"]');
    var qty=q&&parseInt(q.value,10)>0?parseInt(q.value,10):1;
    var data={id:id.value,quantity:qty}, props={}, has=false;
    Array.prototype.forEach.call(form.querySelectorAll('[name^="properties["]'),function(el){
      if(el.type==='checkbox'&&!el.checked)return;
      var k=el.name.slice(el.name.indexOf('[')+1,el.name.lastIndexOf(']'));
      if(el.value){props[k]=el.value;has=true;}
    });
    if(has)data.properties=props;
    return data;
  }
  function settle(cart){ stripThemeClass(); showPill(); refreshCartCount(cart); busy=false; }
  function fallbackAdd(data){
    fetch('/cart/add.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(data)})
      .then(function(r){ if(!r.ok)throw new Error('add failed'); return r.json(); })
      .then(function(){ return getCart(); })
      .then(function(cart){ if(gateLocked()){busy=false;return;} settle(cart); })
      .catch(function(){ busy=false; window.location.href='/cart'; });
  }
  function onClick(e){
    var target=e.target; if(!target||!target.closest)return;
    var inAddForm=!!target.closest('form[action*="/cart/add"]');
    if(!inAddForm && !target.closest('.ebd-root') && !target.closest('.ebt')){
      var cartLink=target.closest(CART_LINK);
      if(cartLink){ e.preventDefault(); openDrawer(); return; }
    }
    if(busy)return; if(gateLocked())return;
    if(denied(target))return;
    var btn=target.closest(ALLOW); if(!btn||denied(btn))return;
    var form=btn.closest('form[action*="/cart/add"]'); if(!form)return;
    var data=payloadFrom(form); if(!data)return;
    busy=true;
    flashButton(btn);
    getCart().then(function(before){
      var beforeCount=before?before.item_count:null;
      var started=new Date().getTime();
      (function poll(){
        if(gateLocked()){busy=false;return;}
        getCart().then(function(after){
          var afterCount=after?after.item_count:null;
          if(beforeCount===null||afterCount===null||afterCount!==beforeCount){ settle(after); return; }
          if(new Date().getTime()-started<MAX_WAIT_MS){ setTimeout(poll,POLL_EVERY_MS); return; }
          fallbackAdd(data);
        });
      })();
    });
  }
  styles();
  suppressThemeCart();
  document.addEventListener('click',onClick,true);
  window.addEventListener('pageshow',function(e){ if(e.persisted)syncCount(); });
  if(document.readyState==='loading'){ document.addEventListener('DOMContentLoaded',syncCount); } else { syncCount(); }
  window.EB_CART_DRAWER=true;
})();